import { Answer } from "@domain/Answer";
import { Exercise } from "@domain/Exercise";
import { SequenceResults } from "@domain/Result";

export class SequenceId {
  readonly value: string;

  constructor(id: string) {
    this.value = id;
  }

  toString(): string {
    return this.value;
  }

  isEqualTo(sequenceId: SequenceId) {
    return this.value === sequenceId.toString();
  }
}

export class Sequence {
  private readonly results: SequenceResults;

  constructor(results: SequenceResults) {
    this.results = results;
  }

  numberOfResults() {
    return this.results.length;
  }

  getResults(): SequenceResults {
    return this.results;
  }

  addResult(exercise: Exercise, answer: Answer): Sequence {
    return new Sequence([
      ...this.results,
      {
        exercise: exercise,
        answer,
        numberOfTries: 1,
      },
    ]);
  }

  updateResult({ id }: Exercise, answer: Answer): Sequence {
    const results = this.results.map(result => {
      if (result.exercise.id.isEqualTo(id)) {
        return {
          ...result,
          numberOfTries: result.numberOfTries + 1,
          answer,
        };
      }

      return result;
    });

    return new Sequence(results);
  }

  getCorrectResults(): SequenceResults {
    return this.results.filter(result => result.answer.isCorrect());
  }

  getIncorrectResults(): SequenceResults {
    return this.results.filter(result => result.answer.isIncorrect());
  }
}

export class CurrentSequence extends Sequence {
  private readonly id: SequenceId;

  constructor(id: SequenceId, results: SequenceResults) {
    super(results);

    this.id = id;
  }

  getId() {
    return this.id;
  }

  override addResult(exercise: Exercise, answer: Answer): Sequence {
    const newSequence = super.addResult(exercise, answer);
    return new CurrentSequence(this.id, newSequence.getResults());
  }

  override updateResult(exercise: Exercise, answer: Answer): Sequence {
    const newSequence = super.updateResult(exercise, answer);
    return new CurrentSequence(this.id, newSequence.getResults());
  }
}
